import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  User,
  Lock,
  CreditCard,
  Bell,
  KeyRound,
  Globe,
  ChevronRight,
  Camera,
  Crown,
  Activity,
  LogOut,
  X,
  Check,
  Building2,
  Mail,
  Wifi,
} from "lucide-react";

const PLAN_INFO = {
  starter: { label: "Starter", price: "R$ 49/mês", color: "text-slate-300" },
  pro: { label: "Pro", price: "R$ 97/mês", color: "text-cp-primary-light" },
  scale: { label: "Scale", price: "R$ 197/mês", color: "text-cp-accent" },
  enterprise: { label: "Enterprise", price: "Sob consulta", color: "text-cp-warning" },
};

const PLAN_LIMITS = {
  starter: { pix: 100, msg: 300, integ: 1, wa: 1 },
  pro: { pix: 500, msg: 2000, integ: 3, wa: 2 },
  scale: { pix: 2000, msg: 10000, integ: 10, wa: 5 },
};

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

/* ============================================================
   USAGE BAR — barra de progresso de uso
   ============================================================ */
function UsageBar({ label, value, max, testid }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const warn = pct >= 80;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 truncate">
        {label}
      </div>
      <div className="text-lg font-bold tabular-nums">
        {value}
        <span className="text-slate-500 text-sm font-normal"> / {max}</span>
      </div>
      <div className="h-1 bg-cp-elevated/40 rounded-full mt-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            warn ? "bg-cp-warning" : "bg-cp-primary"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ============================================================
   MODAL base
   ============================================================ */
function Modal({ open, onClose, title, subtitle, children, testid }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid={testid}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-cp-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="font-heading text-xl font-bold tracking-tight">{title}</h3>
            {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ============================================================
   FIELD — input estilizado com label
   ============================================================ */
function Field({ label, type = "text", value, onChange, placeholder, testid, autoFocus }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testid}
        className="w-full bg-cp-elevated/30 border border-white/[0.08] text-white text-sm rounded-lg px-3.5 py-2.5 focus:outline-none focus:border-cp-primary/50 transition"
      />
    </div>
  );
}

/* ============================================================
   PROFILE MODAL — editar nome, email, empresa
   ============================================================ */
function ProfileModal({ open, onClose, user, tenant, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setCompany(tenant?.name || "");
    }
  }, [open, user, tenant]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const userPayload = {};
      if (name !== user.name) userPayload.name = name;
      if (email !== user.email) userPayload.email = email;
      if (Object.keys(userPayload).length > 0) {
        await api.put("/auth/me", userPayload);
      }
      if (company !== tenant.name) {
        await api.put("/tenant", { name: company });
      }
      toast.success("Perfil atualizado!");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dados pessoais"
      subtitle="Atualize seu nome, email e empresa."
      testid="profile-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome" value={name} onChange={setName} testid="profile-name" autoFocus />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="profile-email" />
        <Field label="Empresa" value={company} onChange={setCompany} testid="profile-company" />
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-white/[0.08] text-sm hover:bg-white/[0.04] transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save-btn"
            className="flex-1 px-4 py-2.5 rounded-lg bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm transition disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================
   PASSWORD MODAL
   ============================================================ */
function PasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Confirmação não bate com a nova senha");
      return;
    }
    if (next.length < 6) {
      toast.error("Nova senha precisa ter pelo menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Senha alterada com sucesso!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Alterar senha"
      subtitle="Use uma senha forte com no mínimo 6 caracteres."
      testid="password-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Senha atual"
          type="password"
          value={current}
          onChange={setCurrent}
          testid="password-current"
          autoFocus
        />
        <Field
          label="Nova senha"
          type="password"
          value={next}
          onChange={setNext}
          testid="password-new"
        />
        <Field
          label="Confirmar nova senha"
          type="password"
          value={confirm}
          onChange={setConfirm}
          testid="password-confirm"
        />
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-white/[0.08] text-sm hover:bg-white/[0.04] transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="password-save-btn"
            className="flex-1 px-4 py-2.5 rounded-lg bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm transition disabled:opacity-50"
          >
            {saving ? "Alterando..." : "Alterar senha"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================
   SOON MODAL — placeholder para áreas em breve
   ============================================================ */
function SoonModal({ open, onClose, title, message }) {
  return (
    <Modal open={open} onClose={onClose} title={title} subtitle="Em breve">
      <p className="text-sm text-slate-400 leading-relaxed">{message}</p>
      <button
        onClick={onClose}
        className="w-full mt-5 px-4 py-2.5 rounded-lg bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm transition"
      >
        Entendi
      </button>
    </Modal>
  );
}

/* ============================================================
   PÁGINA PRINCIPAL
   ============================================================ */
export default function SettingsPage() {
  const { user, logout } = useAuth();
  const ctx = useOutletContext();
  const [tenant, setTenant] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  // modais
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [soon, setSoon] = useState({ open: false, title: "", message: "" });

  const load = async () => {
    try {
      const [t, i] = await Promise.all([
        api.get("/tenant"),
        api.get("/dashboard/insights?period=30d"),
      ]);
      setTenant(t.data);
      setInsights(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openSoon = (title, message) => setSoon({ open: true, title, message });

  // plano: por enquanto fixo no Pro (backend de planos virá depois)
  const planKey = (tenant?.plan || "pro").toLowerCase();
  const plan = PLAN_INFO[planKey] || PLAN_INFO.pro;
  const limits = PLAN_LIMITS[planKey] || PLAN_LIMITS.pro;

  const usage = {
    pix: insights?.recent_transactions
      ? insights.funnel?.find((f) => f.key === "generated")?.value || 0
      : 0,
    msg: insights?.messages_period?.sent || 0,
    integ: insights?.top_integrations?.length || 0,
    wa: insights?.health?.whatsapp_status === "connected" ? 1 : 0,
  };

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (e) {
      // mesmo com erro de servidor, limpa client-side
    }
    window.location.href = "/login";
  };

  if (loading || !tenant) {
    return (
      <div className="space-y-6" data-testid="settings-page-loading">
        <div className="h-10 w-48 bg-cp-elevated/40 rounded animate-pulse" />
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="h-96 bg-cp-elevated/40 rounded-2xl animate-pulse" />
          <div className="lg:col-span-2 h-96 bg-cp-elevated/40 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  const initials = getInitials(user?.name);

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-2">
          _minha conta
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">
          Sua conta
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Identidade, plano, segurança e ações rápidas.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* ========== Card identidade ========== */}
        <div
          className="lg:col-span-1 rounded-2xl border border-white/[0.06] bg-gradient-to-b from-cp-primary/[0.04] to-cp-card/40 p-6 flex flex-col items-center text-center"
          data-testid="account-identity-card"
        >
          <div className="relative mb-4">
            <div className="h-24 w-24 rounded-full bg-cp-primary/15 border-2 border-cp-primary/40 flex items-center justify-center font-heading text-3xl font-bold text-cp-primary-light">
              {initials}
            </div>
            <button
              onClick={() =>
                openSoon(
                  "Foto de perfil",
                  "Em breve você poderá fazer upload da sua foto. Por enquanto usamos suas iniciais."
                )
              }
              data-testid="change-photo-btn"
              className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-cp-primary text-cp-bg flex items-center justify-center shadow-lg shadow-cp-primary/30 hover:bg-cp-primary-hover transition"
              aria-label="Trocar foto"
            >
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <h3 className="font-heading text-xl font-bold" data-testid="account-name">
            {user?.name}
          </h3>
          <div className="text-sm text-slate-400 mt-1 truncate max-w-full" data-testid="account-email">
            {user?.email}
          </div>
          <div
            className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono px-2.5 py-1 rounded-full bg-cp-primary/15 text-cp-primary-light border border-cp-primary/30"
            data-testid="account-plan-badge"
          >
            <Crown className="h-3 w-3" /> Plano {plan.label}
          </div>

          <div className="w-full mt-6 pt-6 border-t border-white/[0.06] space-y-3 text-left">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Membro desde</span>
              <span className="text-slate-300 font-medium">
                {formatDate(user?.created_at || tenant?.created_at)}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Empresa</span>
              <span className="text-slate-300 font-medium truncate ml-2" data-testid="account-company">
                {tenant.name}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Cargo</span>
              <span className="text-slate-300 font-medium capitalize">{user?.role}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">WhatsApp</span>
              <span
                className={`inline-flex items-center gap-1.5 font-medium ${
                  insights?.health?.whatsapp_status === "connected"
                    ? "text-cp-primary-light"
                    : "text-slate-400"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    insights?.health?.whatsapp_status === "connected"
                      ? "bg-cp-primary animate-pulse"
                      : "bg-slate-500"
                  }`}
                />
                {insights?.health?.whatsapp_status === "connected"
                  ? "Conectado"
                  : "Desconectado"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Modo da conta</span>
              <span className="text-slate-300 font-medium capitalize">
                {tenant.mode}
              </span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full mt-6 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 border border-red-500/20 rounded-lg py-2.5 transition inline-flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" /> Sair da conta
          </button>
        </div>

        {/* ========== Right column ========== */}
        <div className="lg:col-span-2 space-y-4">
          {/* Plano usage */}
          <div
            className="rounded-2xl border border-cp-primary/30 bg-gradient-to-br from-cp-primary/[0.06] to-transparent p-5"
            data-testid="plan-usage-card"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-cp-primary-light" />
                <div>
                  <div className="font-heading font-semibold">Uso este mês</div>
                  <div className="text-xs text-slate-500">
                    Plano <span className={plan.color}>{plan.label}</span> · {plan.price}
                  </div>
                </div>
              </div>
              <button
                onClick={() =>
                  openSoon(
                    "Trocar plano",
                    "A página de assinatura e cobrança chega em breve. Por enquanto, fale com nosso time pra mudar de plano."
                  )
                }
                data-testid="change-plan-btn"
                className="text-xs bg-cp-primary text-cp-bg font-semibold px-3 py-1.5 rounded-md hover:bg-cp-primary-hover transition"
              >
                Trocar plano
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <UsageBar label="PIX monitorados" value={usage.pix} max={limits.pix} testid="usage-pix" />
              <UsageBar label="Mensagens" value={usage.msg} max={limits.msg} testid="usage-msg" />
              <UsageBar label="Integrações" value={usage.integ} max={limits.integ} testid="usage-integ" />
              <UsageBar label="WhatsApps" value={usage.wa} max={limits.wa} testid="usage-wa" />
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid sm:grid-cols-2 gap-3">
            <ActionCard
              icon={User}
              title="Dados pessoais"
              desc="Nome, email, empresa"
              onClick={() => setProfileOpen(true)}
              testid="action-profile"
            />
            <ActionCard
              icon={Lock}
              title="Senha & Segurança"
              desc="Alterar senha, 2FA em breve"
              onClick={() => setPasswordOpen(true)}
              testid="action-password"
            />
            <ActionCard
              icon={CreditCard}
              title="Plano & Cobrança"
              desc={`${plan.label} · ${plan.price}`}
              onClick={() =>
                openSoon(
                  "Plano & Cobrança",
                  "Estamos finalizando o checkout integrado. Em breve você troca, faz upgrade e gerencia faturas direto por aqui."
                )
              }
              testid="action-plan"
            />
            <ActionCard
              icon={Bell}
              title="Notificações"
              desc="Alertas por email e WhatsApp"
              onClick={() =>
                openSoon(
                  "Notificações",
                  "Você poderá configurar alertas: PIX recuperado, queda de taxa, WhatsApp desconectado, e mais."
                )
              }
              testid="action-notifications"
            />
            <ActionCard
              icon={KeyRound}
              title="API & Webhooks"
              desc="Tokens e integração programática"
              link="/app/integrations"
              testid="action-api"
            />
            <ActionCard
              icon={Globe}
              title="Idioma & Região"
              desc="Português · BRL"
              onClick={() =>
                openSoon(
                  "Idioma & Região",
                  "Por enquanto a ConvertPix opera em português (BR) e BRL. Suporte multi-idioma chega em breve."
                )
              }
              testid="action-region"
            />
          </div>

          {/* Connection quick info */}
          <Link
            to="/app/connection"
            data-testid="connection-shortcut"
            className="flex items-center gap-3 p-4 rounded-xl border border-white/[0.06] bg-cp-card/40 hover:border-white/[0.15] transition group"
          >
            <div className="h-10 w-10 rounded-lg bg-cp-elevated/40 border border-white/[0.06] flex items-center justify-center">
              <Wifi className="h-4 w-4 text-cp-primary-light" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">WhatsApp & Conexão</div>
              <div className="text-xs text-slate-500 truncate">
                {insights?.health?.whatsapp_status === "connected"
                  ? "Conectado e enviando mensagens"
                  : "Conectar WhatsApp pra começar a recuperar PIX"}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-cp-primary-light transition" />
          </Link>
        </div>
      </div>

      {/* Modais */}
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        tenant={tenant}
        onSaved={async () => {
          await load();
          await ctx?.reloadStatus?.();
        }}
      />
      <PasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      <SoonModal
        open={soon.open}
        onClose={() => setSoon({ open: false, title: "", message: "" })}
        title={soon.title}
        message={soon.message}
      />
    </div>
  );
}

/* ============================================================
   ACTION CARD
   ============================================================ */
function ActionCard({ icon: Icon, title, desc, onClick, link, testid }) {
  const inner = (
    <>
      <div className="h-9 w-9 rounded-lg bg-cp-elevated/40 border border-white/[0.06] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-cp-primary-light" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{title}</div>
        <div className="text-[11px] text-slate-500 truncate">{desc}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-cp-primary-light transition" />
    </>
  );

  const cls =
    "rounded-xl border border-white/[0.06] bg-cp-card/40 p-4 hover:border-cp-primary/20 transition cursor-pointer group flex items-center gap-3 w-full text-left";

  if (link) {
    return (
      <Link to={link} className={cls} data-testid={testid}>
        {inner}
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testid}>
      {inner}
    </button>
  );
}
